import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import React, { useState, useEffect } from 'react';
import { Globe, RefreshCw, WifiOff } from 'lucide-react';
import { DMButton, DMDiv, DMText, DMIframe } from './Primitives';

const WebsitePreviewFallback = ({ onRetry }: { onRetry: () => void }) => (
  <DMDiv className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50/60 p-[2vh]">
    {/* Decorative background pattern */}
    <DMDiv
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
        backgroundSize: '2.4vh 2.4vh',
      }}
    />

    {/* Browser chrome mockup */}
    <DMDiv className="relative w-[75%] max-w-[32vh]">
      <DMDiv className="flex h-[3.2vh] items-center gap-[0.6vh] rounded-t-[1vh] border border-b-0 border-gray-200/80 bg-white/90 px-[1.2vh] shadow-sm backdrop-blur-sm">
        <DMText
          as="span"
          className="h-[0.9vh] w-[0.9vh] rounded-full bg-[#FF5F57]"
        />
        <DMText
          as="span"
          className="h-[0.9vh] w-[0.9vh] rounded-full bg-[#FEBC2E]"
        />
        <DMText
          as="span"
          className="h-[0.9vh] w-[0.9vh] rounded-full bg-[#28C840]"
        />
        <DMDiv className="ml-[0.8vh] h-[1.2vh] flex-1 rounded-full bg-gray-100" />
      </DMDiv>
      <DMDiv className="flex h-[12vh] items-center justify-center rounded-b-[1vh] border border-gray-200/80 bg-white/70 shadow-sm backdrop-blur-sm">
        <DMDiv className="flex h-[5vh] w-[5vh] items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-50 shadow-inner">
          <WifiOff
            className="h-[2.6vh] w-[2.6vh] text-gray-300"
            strokeWidth={1.8}
          />
        </DMDiv>
      </DMDiv>
    </DMDiv>

    {/* Message */}
    <DMDiv className="relative mt-[2.5vh] text-center">
      <DMText className="text-[1.7vh] font-semibold leading-tight text-gray-700">
        Preview Unavailable
      </DMText>
      <DMText className="mt-[0.8vh] text-[1.3vh] font-medium leading-snug text-gray-400">
        Website couldn't be reached right now
      </DMText>
    </DMDiv>

    {/* Retry button */}
    <DMButton
      type="button"
      onClick={onRetry}
      className="relative mt-[2vh] flex items-center gap-[0.7vh] rounded-full border border-gray-200 bg-white px-[2.2vh] py-[1vh] text-[1.35vh] font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-primary-main/40 hover:text-primary-main hover:shadow-md active:scale-[0.97]"
    >
      <RefreshCw className="h-[1.6vh] w-[1.6vh]" strokeWidth={2.2} />
      Retry
    </DMButton>
  </DMDiv>
);

export const ResponsiveWebsiteCard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [siteReachable, setSiteReachable] = useState<boolean | null>(null);
  const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop'
  );
  const authData = useAuth();

  const retryKeyRef = React.useRef(0);

  const WEBSITE_URL = `https://${authData?.BRANCH_DOMAIN}/`;

  // Reachability check via fetch before attempting iframe
  useEffect(() => {
    if (siteReachable !== null) return;

    const controller = new AbortController();

    fetch(WEBSITE_URL, { mode: 'no-cors', signal: controller.signal })
      .then(() => {
        setSiteReachable(true);
      })
      .catch(() => {
        setSiteReachable(false);
        setHasError(true);
        setIsLoading(false);
      });

    // Fallback timeout in case fetch hangs
    const timer = setTimeout(() => {
      controller.abort();
      setSiteReachable(false);
      setHasError(true);
      setIsLoading(false);
    }, 10000);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [siteReachable, WEBSITE_URL]);

  // Responsiveness Animation Loop
  useEffect(() => {
    if (!isLoading && !hasError) {
      const interval = setInterval(() => {
        setViewMode(prev => {
          if (prev === 'desktop') return 'tablet';
          if (prev === 'tablet') return 'mobile';
          return 'desktop';
        });
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isLoading, hasError]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    retryKeyRef.current += 1;
    setSiteReachable(null); // re-trigger the fetch check
    setHasError(false);
    setIsLoading(true);
    setViewMode('desktop');
  };

  const getIframeStyles = () => {
    let scale = '0.3333';
    let dimension = '300%';

    if (viewMode === 'tablet') {
      scale = '0.5';
      dimension = '200%';
    } else if (viewMode === 'mobile') {
      scale = '1';
      dimension = '100%';
    }

    return {
      left: '50%',
      width: dimension,
      height: dimension,
      transform: `translateX(-50%) scale(${scale})`,
      transformOrigin: 'top center',
      opacity: isLoading ? 0 : 1,
      transition:
        'opacity 0.5s ease-in-out, width 2s ease-in-out, height 2s ease-in-out, transform 2s ease-in-out',
    };
  };

  const getBadgeText = () => {
    if (hasError) return 'Status: Offline';
    if (viewMode === 'mobile') return 'View: Mobile';
    if (viewMode === 'tablet') return 'View: Tablet';
    return 'View: Desktop';
  };

  return (
    <DMDiv className="bg-white rounded-[2vh] border border-gray-200 shadow-sm w-full h-full relative overflow-hidden">
      {/* Top-right action button */}
      <DMDiv className="absolute top-[2vh] right-[2vh] z-20 flex items-center justify-center w-[5.4vh] h-[5.4vh] rounded-full overflow-hidden shadow-lg hover:scale-110 transition-transform cursor-pointer">
        {/* Spinning Gradient Background */}
        <DMDiv className="absolute inset-[-50%] bg-gradient-to-r from-[#e55232] via-[#8532d7] to-[#e55232] animate-[spin_3s_linear_infinite]" />

        {/* Inner Button */}
        <DMButton
          onClick={() => window.open(WEBSITE_URL, '_blank')}
          className="w-[4.8vh] h-[4.8vh] relative z-10 bg-white rounded-full flex items-center justify-center text-gray-700 hover:text-primary-main transition-colors"
        >
          <Globe className="w-[2.2vh] h-[2.2vh]" strokeWidth={2} />
        </DMButton>
      </DMDiv>

      {/* Skeleton Loader Overlay */}
      {isLoading && !hasError && (
        <DMDiv className="absolute inset-0 z-10 bg-gray-50 flex flex-col items-center justify-center animate-pulse">
          <DMDiv className="w-[6vh] h-[6vh] border-4 border-gray-300 border-t-primary-main rounded-full animate-spin mb-[2vh]"></DMDiv>
          <DMDiv className="h-[2vh] bg-gray-300 rounded w-1/3 mb-[1vh]"></DMDiv>
          <DMDiv className="h-[1.5vh] bg-gray-200 rounded w-1/2"></DMDiv>
        </DMDiv>
      )}

      {hasError && <WebsitePreviewFallback onRetry={handleRetry} />}

      {/* Iframe content — only mount after site confirmed reachable */}
      {siteReachable && !hasError && (
        <DMIframe
          key={retryKeyRef.current}
          src={WEBSITE_URL}
          className="absolute top-0 border-none z-0 pointer-events-none"
          style={getIframeStyles()}
          title="Responsive Website"
          sandbox="allow-scripts allow-same-origin"
          scrolling="no"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* Status Badge */}
      <DMDiv className="absolute bottom-[2vh] left-0 w-full flex justify-center z-20 pointer-events-none">
        <DMText
          as="span"
          className="inline-block px-[2.5vh] py-[1.2vh] bg-gradient-to-r from-[#e55232] to-[#8532d7] text-white rounded-full text-[1.6vh] font-bold shadow-lg leading-tight pointer-events-auto"
        >
          {getBadgeText()}
        </DMText>
      </DMDiv>
    </DMDiv>
  );
};

